import React from 'react';
import { toast } from './ui/sonner';

/**
 * A self-healing error boundary for the Toaster (#603).
 *
 * The Toaster is a sibling of <AppRouter /> and index.js mounts <App /> with no
 * boundary above it, so a toast whose title is not a valid React child (an
 * un-normalised FastAPI 422 `detail`) threw where nothing caught it and React
 * unmounted the entire tree — a blank page, not the "Something went wrong"
 * panel. errorText() removes the trigger at the call sites that have been
 * migrated; this contains the ~223 that have not.
 *
 * Self-healing on purpose: a latching boundary would silently kill every future
 * toast for the session. Render nothing for the error commit, dismiss the queue,
 * then remount with a fresh key — so one malformed toast costs one toast.
 *
 * getDerivedStateFromError MUST return a state that renders something safe. It
 * runs in the render phase; componentDidCatch (which holds the dismiss) does not
 * run until commit. A boundary that returned {} would re-render the same child
 * BEFORE the queue was cleared — that is how an error boundary turns one bad
 * toast into a render loop. Returning null for that one commit breaks it.
 *
 * The key bump is a real recovery, not cosmetic: sonner's Observer.subscribe
 * only pushes the subscriber (it does not replay its queue) and Toaster
 * initialises with useState([]), so the remounted Toaster starts empty and
 * cannot re-render the toast that threw. Both verified against sonner 2.0.7.
 *
 * Not reusing an existing boundary, so review does not re-ask: App.js's
 * ErrorBoundary latches and paints a full-width error panel — mounted here, a
 * bad toast would paint that panel over the app. ProviderErrorBoundary latches
 * too, recovers only on a user's Retry click, and renders branded provider UI.
 * A Toaster boundary must render NOTHING and recover WITHOUT a click.
 *
 * `children` is keyed by `seed` so the remount is a genuine unmount/mount.
 *
 * MAX_RECOVERIES is the one thing self-healing needs that latching does not.
 * The heal cycle is render → throw → dismiss → remount → render. That
 * terminates only because the remounted Toaster cannot re-render the toast that
 * threw (fresh `useState([])`, no replay). If some future cause DID survive a
 * remount, an unbounded boundary would spin that cycle forever and peg the CPU
 * — the failure it exists to prevent, in a new costume. After three consecutive
 * failures it stops healing and renders nothing: toasts are lost for the rest of
 * the session, but the app stays alive and responsive. A single success is not
 * tracked (there is no "recovered" signal to hook), so the counter is only ever
 * reset by a reload — three strikes is per session, deliberately.
 */
const MAX_RECOVERIES = 3;

export default class ToasterBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { crashed: false, seed: 0, recoveries: 0 };
    }

    static getDerivedStateFromError() {
        return { crashed: true };
    }

    componentDidCatch(error) {
        toast.dismiss();
        this.setState((s) => {
            if (s.recoveries >= MAX_RECOVERIES) {
                console.error(
                    `Toast render failed ${s.recoveries} times; giving up. Toasts are disabled until reload.`,
                    error
                );
                return { crashed: true, seed: s.seed, recoveries: s.recoveries };
            }
            console.error('Toast render failed; queue dismissed.', error);
            return { crashed: false, seed: s.seed + 1, recoveries: s.recoveries + 1 };
        });
    }

    render() {
        if (this.state.crashed) return null;
        return <React.Fragment key={this.state.seed}>{this.props.children}</React.Fragment>;
    }
}
